



































import {
  getIresDeviceCategory,
  IresDevice,
  IresDeviceCategory,
  isActiveProgramChainOfIresDevice,
} from "@/data/models/IresDevice";
import { Vue, Component, Prop, Watch, Ref } from "vue-property-decorator";
import { Panel, isEdgePanel } from "../../data/models/Panel";
import { ProgramChain } from "../../data/models/ProgramChain";
import { ComBox, isHyDryMiniSerial } from "@/data/models/ComBox";

import SelectionTableComponent, {
  TargetSensors,
  LegendDisplayMode,
} from "@/components/common/SelectionTable.vue";
import {
  getComBoxHumidityUnitName,
  toggleComBoxHumidityUnit,
} from "@/data/models/ComBoxHumidityUnit";
import { COLOR_BUTTON_RED } from "@/utils/colors";
import { themedFill } from "@/utils/Theme";
import { getComBoxHumidityUnit, setComBoxHumidityUnit } from "@/data/repository/SettingsRepository";
import { WeatherInformationDetailsLevel } from "@/utils/DryingGraphYAxis";
import { viewIntensityAccess } from "@/permissions/UserPermissions";
import { SensorLegendMetadata } from "@/data/models/SensorLegendMetadata";
import { getEffectiveUser } from "@/utils/ViewAs";

@Component({
  components: {
    "selection-table": SelectionTableComponent,
  },
})
export default class DryingProgressGraphLegendComponent extends Vue {
  @Prop() iresDevice!: IresDevice;
  @Prop() selectedProgramChain!: ProgramChain;
  @Prop() isShowingComBoxDetails!: boolean;
  @Prop({ default: true }) showAbsoluteProgramChainNumber!: boolean;
  @Prop({ default: false }) showOverviewChainOption!: boolean;
  @Prop({ default: true }) preferAbsHumidity!: boolean;
  @Prop({ default: () => new Map() }) sensorLegendMetadata!: Map<number, SensorLegendMetadata>;
  @Prop({ default: undefined }) externalWeatherLevel!: WeatherInformationDetailsLevel | undefined;
  @Prop({ default: false }) isWeatherLoading!: boolean;
  /** Pass false on a screen that draws the graph without weather. @see SelectionTable.showWeatherOption */
  @Prop({ default: true }) showWeatherOption!: boolean;

  @Ref("sensorSelectionTable")
  selectionTable!: SelectionTableComponent;

  currentLegendMode = LegendDisplayMode.OVERLAY;

  /** An action: the brand red, the accent in dark mode. A getter so a theme switch repaints it. */
  get toggleComBoxHumidityUnitButtonColor() {
    return themedFill(COLOR_BUTTON_RED);
  }

  /** The user whose view this shows: the chosen one while "Sicht auf" is active (ticket 2610052). */
  get activeUser() {
    return getEffectiveUser();
  }

  get projectId() {
    return this.iresDevice.currentAssignment?.currentProjectId ?? null;
  }

  get isMini() {
    return isHyDryMiniSerial(this.iresDevice.serialNo);
  }

  /**
   * Whether the selected chain is the ComBox's running drying.
   *
   * Everything the legend takes from the live device status hangs off this: a finished drying must
   * not be described by the box's current state, and sensor info is not shown for one at all.
   */
  get isActiveComBoxDrying(): boolean {
    return (
      getIresDeviceCategory(this.iresDevice) === IresDeviceCategory.COMBOX &&
      isActiveProgramChainOfIresDevice(this.iresDevice, this.selectedProgramChain)
    );
  }

  /**
   * comBoxStatus.sensorFlagsExt — the live per-slot sensor flags, only provided for active dryings.
   *
   * Undefined for firmware older than v4.0.0, which sends no flags at all; the legend then shows no
   * warning, which is the same thing it showed before flags existed.
   */
  get activeSensorFlags(): number[] | undefined {
    return this.isActiveComBoxDrying
      ? (this.iresDevice as ComBox).comBoxStatus?.sensorFlagsExt
      : undefined;
  }

  /** comBoxStatus.sensorSlotSerials — only provided for active dryings. */
  get activeFallbackSerials(): Record<string, string> | undefined {
    return this.isActiveComBoxDrying
      ? (this.iresDevice as ComBox).comBoxStatus?.sensorSlotSerials
      : undefined;
  }

  get showPanelLegend() {
    return getIresDeviceCategory(this.iresDevice) == IresDeviceCategory.PANEL;
  }

  get comBoxHumidityUnit() {
    return getComBoxHumidityUnit();
  }

  @Watch("selectedProgramChain", { immediate: true })
  onProgramChainChanged(
    newValue: ProgramChain,
    previousValue: ProgramChain | undefined
  ) {
    if (newValue.id !== previousValue?.id) {
      this.$nextTick(() => {
        this.selectionTable.showSensorSelectionOptionsForProgramChain(newValue, this.isAllowedToViewIntensity);
      });
    }
  }

  @Watch("isAllowedToViewIntensity", { immediate: true })
  onIsAllowedToViewIntensityChanged() {
    this.selectionTable?.showSensorSelectionOptionsForProgramChain(this.selectedProgramChain, this.isAllowedToViewIntensity);
  }

  get iresDeviceCategory() {
    return getIresDeviceCategory(this.iresDevice);
  }

  get isAllowedToViewIntensity() {
    return this.showPanelInfo && viewIntensityAccess(this.activeUser);
  }

  get showPanelInfo() {
    return this.iresDeviceCategory === IresDeviceCategory.PANEL;
  }

  get showComBoxInfo() {
    return this.iresDeviceCategory === IresDeviceCategory.COMBOX;
  }

  get showGroundTempLabel() {
    return this.showPanelInfo ? isEdgePanel(this.iresDevice as Panel) : false;
  }

  onTargetSensorsChanged(targetSensors: TargetSensors) {
    this.$emit("onTargetSensorsChanged", targetSensors);
  }

  onShowPowerInformationChanged(showPowerLine: boolean) {
    this.$emit("onShowPowerInformationChanged", showPowerLine);
  }

  onWeatherInformationDetailsLevelChanged(weatherInformationDetailsLevel: WeatherInformationDetailsLevel) {
    this.$emit("onWeatherInformationDetailsLevelChanged", weatherInformationDetailsLevel);
  }

  onLegendDisplayModeChanged(mode: LegendDisplayMode) {
    this.currentLegendMode = mode;
    this.$emit("onLegendDisplayModeChanged", mode);
  }

  onLegendHover(colors: string[] | null) {
    this.$emit("onLegendHover", colors);
  }

  get sensorSelectClass(): string {
    return this.currentLegendMode === LegendDisplayMode.BELOW
      ? 'sensor-select-below'
      : 'sensor-select';
  }

  onToggleComBoxHumidityUnit() {
    const newComBoxHumidityUnit = toggleComBoxHumidityUnit(
      this.comBoxHumidityUnit,
      this.preferAbsHumidity
    );

    setComBoxHumidityUnit(newComBoxHumidityUnit);
    
    this.$emit("onComBoxHumidityChanged");
  }

  get comBoxHumidityUnitName() {
    return getComBoxHumidityUnitName(
      this,
      this.comBoxHumidityUnit
    );
  }
}
